import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Plus, Calendar, Search, Edit, Trash2, Play, Pause, Clock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import Layout from '../../components/Layout';
import { pmSchedulesAPI, placesAPI, categoriesAPI, usersAPI } from '../../lib/api';
import { formatDateOnly, localDateInputValue } from '../../lib/utils';
import { assigneeLabel } from '../../lib/publicSite';


const PMSchedulesPage = () => {
  const { user } = useAuth();
  const [schedules, setSchedules] = useState([]);
  const [places, setPlaces] = useState([]);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    place_id: '',
    category_id: '',
    assigned_to: '',
    frequency: 'weekly',
    start_date: localDateInputValue(),
    is_active: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [schedulesRes, placesRes, categoriesRes, usersRes] = await Promise.all([
        pmSchedulesAPI.getAll(),
        placesAPI.getAll(),
        categoriesAPI.getAll(),
        usersAPI.getAll()
      ]);
      
      setSchedules(schedulesRes.data || []);
      setPlaces(placesRes.data || []);
      setCategories(categoriesRes.data || []);
      setUsers(usersRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      if (error.response?.status !== 401) toast.error('Failed to load PM schedules');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.description) {
      toast.error('Title and description are required');
      return;
    }
    
    try {
      const response = await pmSchedulesAPI.create(formData);
      
      setSchedules([response.data, ...schedules]);
      toast.success('PM schedule created successfully');
      setCreateDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Failed to create schedule:', error);
      toast.error(error.response?.data?.detail || 'Failed to create schedule');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    
    try {
      const response = await pmSchedulesAPI.update(selectedSchedule.id, formData);
      
      setSchedules(schedules.map(s => s.id === selectedSchedule.id ? response.data : s));
      toast.success('Schedule updated successfully');
      setEditDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Failed to update schedule:', error);
      toast.error(error.response?.data?.detail || 'Failed to update schedule');
    }
  };

  const handleDelete = async (scheduleId) => {
    if (!window.confirm('Are you sure you want to delete this PM schedule?')) return;
    
    try {
      await pmSchedulesAPI.delete(scheduleId);
      
      setSchedules(schedules.filter(s => s.id !== scheduleId));
      toast.success('Schedule deleted');
    } catch (error) {
      console.error('Failed to delete schedule:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete schedule');
    }
  };

  const handleToggleActive = async (scheduleId, currentStatus) => {
    try {
      await pmSchedulesAPI.update(scheduleId, { is_active: !currentStatus });
      
      setSchedules(schedules.map(s => 
        s.id === scheduleId ? { ...s, is_active: !currentStatus } : s
      ));
      
      toast.success(`Schedule ${!currentStatus ? 'activated' : 'paused'}`);
    } catch (error) {
      console.error('Failed to toggle schedule:', error);
      toast.error('Failed to update schedule status');
    }
  };

  const handleGenerateWorkOrders = async () => {
    try {
      const response = await pmSchedulesAPI.generateWorkOrders();
      
      toast.success(`Generated ${response.data.generated} work orders`);
    } catch (error) {
      console.error('Failed to generate work orders:', error);
      toast.error('Failed to generate work orders');
    }
  };

  const openEditDialog = (schedule) => {
    setSelectedSchedule(schedule);
    setFormData({
      title: schedule.title,
      description: schedule.description,
      place_id: schedule.place_id || '',
      category_id: schedule.category_id || '',
      assigned_to: schedule.assigned_to || '',
      frequency: schedule.frequency,
      start_date: schedule.start_date,
      is_active: schedule.is_active
    });
    setEditDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      place_id: '',
      category_id: '',
      assigned_to: '',
      frequency: 'weekly',
      start_date: localDateInputValue(),
      is_active: true
    });
    setSelectedSchedule(null);
  };

  const filteredSchedules = schedules.filter(s => 
    s.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getFrequencyBadge = (frequency) => {
    const colors = {
      daily: 'bg-blue-100 text-blue-800',
      weekly: 'bg-green-100 text-green-800',
      monthly: 'bg-purple-100 text-purple-800',
      quarterly: 'bg-orange-100 text-orange-800',
      yearly: 'bg-red-100 text-red-800'
    };
    return colors[frequency] || colors.weekly;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-gray-500">Loading PM schedules...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Preventive Maintenance</h1>
          <p className="text-slate-300 mt-1">Schedule recurring maintenance tasks</p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleGenerateWorkOrders}>
            <Clock className="w-4 h-4 mr-2" />
            Generate Work Orders
          </Button>
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-amber-500 hover:bg-amber-600">
                <Plus className="w-4 h-4 mr-2" />
                Create Schedule
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl bg-slate-900 text-white border-slate-700">
              <DialogHeader>
                <DialogTitle>Create PM Schedule</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4 mt-4">
                <div>
                  <Label>Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder="e.g., Monthly HVAC Inspection"
                    required
                  />
                </div>
                
                <div>
                  <Label>Description *</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Detailed description of maintenance task..."
                    required
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Location</Label>
                    <Select value={formData.place_id} onValueChange={(val) => setFormData({...formData, place_id: val})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select location" />
                      </SelectTrigger>
                      <SelectContent className="z-[100]">
                        <SelectItem value="none">No location</SelectItem>
                        {places.map(p => (
                          <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>Category</Label>
                    <Select value={formData.category_id} onValueChange={(val) => setFormData({...formData, category_id: val})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent className="z-[100]">
                        <SelectItem value="none">No category</SelectItem>
                        {categories.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Frequency *</Label>
                    <Select value={formData.frequency} onValueChange={(val) => setFormData({...formData, frequency: val})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="z-[100]">
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                        <SelectItem value="yearly">Yearly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>Start Date *</Label>
                    <Input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <Label>Assigned To</Label>
                  <Select value={formData.assigned_to} onValueChange={(val) => setFormData({...formData, assigned_to: val})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Unassigned" />
                    </SelectTrigger>
                    <SelectContent className="z-[100]">
                      <SelectItem value="none">Unassigned</SelectItem>
                      {users.filter(u => u.role === 'technician' || u.role === 'supervisor').map(u => (
                        <SelectItem key={u.id} value={u.id}>{assigneeLabel(u, users)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
                  />
                  <Label>Active</Label>
                </div>
                
                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-amber-500 hover:bg-amber-600">
                    Create Schedule
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
        <Input
          placeholder="Search schedules..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Schedules List */}
      {filteredSchedules.length === 0 ? (
        <div className="text-center py-12 bg-slate-900 border border-slate-700 rounded-sm">
          <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-white">No PM schedules found</p>
          <p className="text-sm text-slate-300 mt-1">Create your first schedule to automate maintenance</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredSchedules.map((schedule) => (
            <div key={schedule.id} className="bg-slate-900 border border-slate-700 rounded-sm p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-semibold text-white">{schedule.title}</h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getFrequencyBadge(schedule.frequency)}`}>
                      {schedule.frequency.toUpperCase()}
                    </span>
                    {schedule.is_active ? (
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        ACTIVE
                      </span>
                    ) : (
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        PAUSED
                      </span>
                    )}
                  </div>
                  <p className="text-slate-200 mb-4">{schedule.description}</p>
                  
                  <div className="flex items-center gap-6 text-sm text-slate-300 flex-wrap">
                    {schedule.place_name && (
                      <div className="flex items-center gap-1">
                        <span className="font-medium">Location:</span> {schedule.place_name}
                      </div>
                    )}
                    {schedule.category_name && (
                      <div className="flex items-center gap-1">
                        <span className="font-medium">Category:</span> {schedule.category_name}
                      </div>
                    )}
                    {schedule.assigned_to_name && (
                      <div className="flex items-center gap-1">
                        <span className="font-medium">Assigned:</span> {schedule.assigned_to_name}
                      </div>
                    )}
                    <div className="flex items-center gap-1">
                      <span className="font-medium">Starts:</span> {formatDateOnly(schedule.start_date)}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleToggleActive(schedule.id, schedule.is_active)}
                  >
                    {schedule.is_active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => openEditDialog(schedule)}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(schedule.id)}
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl bg-slate-900 text-white border-slate-700">
          <DialogHeader>
            <DialogTitle>Edit PM Schedule</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4 mt-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                required
              />
            </div>
            
            <div>
              <Label>Description *</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                required
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Frequency *</Label>
                <Select value={formData.frequency} onValueChange={(val) => setFormData({...formData, frequency: val})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="z-[100]">
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="yearly">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Assigned To</Label>
                <Select value={formData.assigned_to || 'none'} onValueChange={(val) => setFormData({...formData, assigned_to: val === 'none' ? '' : val})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Unassigned" />
                  </SelectTrigger>
                  <SelectContent className="z-[100]">
                    <SelectItem value="none">Unassigned</SelectItem>
                    {users.filter(u => u.role === 'technician' || u.role === 'supervisor').map(u => (
                      <SelectItem key={u.id} value={u.id}>{assigneeLabel(u, users)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
              />
              <Label>Active</Label>
            </div>
            
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-amber-500 hover:bg-amber-600">
                Update Schedule
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
    </Layout>
  );
};

export default PMSchedulesPage;